import { useState } from "react";
import { NavLink } from "react-router-dom";
import { AdminSidebarIcons } from "./Sidebar/AdminSidebarIcons";
import { getVisibleAdminMenu } from "../helper/adminMenu.helpers";
import { useAdminAuth } from "../context/AdminAuthContext";

function SidebarIcon({ iconKey, iconClass, label }) {
    if (iconKey && AdminSidebarIcons[iconKey]) {
        return (
            <i
                className=""
                data-bs-toggle="tooltip"
                title={label}
                data-bs-placement="right"
            >
                {AdminSidebarIcons[iconKey]}
            </i>
        );
    }

    return (
        <i
            className={`icon ${iconClass || "feather-circle"}`}
            data-bs-toggle="tooltip"
            title={label}
            data-bs-placement="right"
        />
    );
}

function Sidebar() {
    const SIDEBAR_STORAGE_KEY = "admin_sidebar_mini";

    const { admin } = useAdminAuth();

    const [isSidebarMini, setIsSidebarMini] = useState(() => {
        return localStorage.getItem(SIDEBAR_STORAGE_KEY) === "true";
    });

    const handleToggleSidebar = () => {
        setIsSidebarMini((prev) => {
            const nextValue = !prev;
            localStorage.setItem(SIDEBAR_STORAGE_KEY, String(nextValue));
            return nextValue;
        });
    };

    const visibleMenu = getVisibleAdminMenu(admin);

    return (
        <aside
            className={`sidebar sidebar-base ${isSidebarMini ? "sidebar-mini" : ""}`}
            id="first-tour"
            data-toggle="main-sidebar"
            data-sidebar="responsive"
        >
            <div className="sidebar-header d-flex align-items-center justify-content-start">
                <NavLink to="/" className="navbar-brand">
                    <div className="logo-main" style={{ width: "80%" }}>
                        <div className="logo-normal">
                            <img
                                src="./assets/images/logo-CORE-by-panduit.png"
                                className="img-fluid"
                                alt="Panduit Core"
                                loading="lazy"
                            />
                        </div>
                        <div className="logo-mini">
                            <img
                                src="./assets/images/logo-CORE-by-panduit.png"
                                className="img-fluid"
                                alt="Panduit Core"
                                loading="lazy"
                            />
                        </div>
                    </div>
                </NavLink>

                <div
                    className={`sidebar-toggle ${isSidebarMini ? "active" : ""}`}
                    data-toggle="sidebar"
                    data-active={isSidebarMini ? "true" : "false"}
                    onClick={handleToggleSidebar}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            handleToggleSidebar();
                        }
                    }}
                >
                    <i className="icon">
                        <svg
                            className="icon-20"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M4.25 12.2744L19.25 12.2744"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <path
                                d="M10.2998 18.2988L4.2498 12.2748L10.2998 6.24976"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </i>
                </div>
            </div>

            <div className="sidebar-body pt-0 data-scrollbar">
                <div
                    className="sidebar-list"
                    style={{
                        maxHeight: "100vh",
                        overflowY: "auto",
                        overflowX: "hidden",
                    }}
                >
                    <ul className="navbar-nav iq-main-menu" id="sidebar-menu">
                        {visibleMenu.map((section) => (
                            <MenuSection key={section.section} section={section} />
                        ))}
                    </ul>
                </div>
            </div>
        </aside>
    );
}

function MenuSection({ section }) {
    return (
        <>
            <li className="nav-item static-item">
                <a
                    className="nav-link static-item disabled text-start"
                    href="#!"
                    tabIndex="-1"
                >
                    <span className="default-icon">{section.section}</span>
                    <span className="mini-icon">-</span>
                </a>
            </li>

            {section.items.map((item) => {
                if (item.type === "group") {
                    return <MenuGroup key={item.id} item={item} />;
                }

                return <MenuLink key={item.path} item={item} />;
            })}

            <li>
                <hr className="hr-horizontal" />
            </li>
        </>
    );
}

function MenuGroup({ item }) {
    return (
        <li className="nav-item">
            <a
                className="nav-link"
                data-bs-toggle="collapse"
                href={`#${item.id}`}
                role="button"
                aria-expanded="false"
                aria-controls={item.id}
            >
                <SidebarIcon
                    iconKey={item.iconKey}
                    iconClass={item.icon}
                    label={item.label}
                />

                <span className="item-name">{item.label}</span>

                <i className="right-icon">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        className="icon-18"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M9 5l7 7-7 7"
                        />
                    </svg>
                </i>
            </a>

            <ul className="sub-nav collapse" id={item.id} data-bs-parent="#sidebar-menu">
                {item.children.map((child) => (
                    <li className="nav-item" key={child.path}>
                        <MenuSubLink item={child} />
                    </li>
                ))}
            </ul>
        </li>
    );
}

function MenuLink({ item }) {
    return (
        <li className="nav-item">
            <NavLink
                to={item.path}
                className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
            >
                <SidebarIcon
                    iconKey={item.iconKey}
                    iconClass={item.icon}
                    label={item.label}
                />

                <span className="item-name">{item.label}</span>
            </NavLink>
        </li>
    );
}

function MenuSubLink({ item }) {
    return (
        <NavLink
            to={item.path}
            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
        >
            <i className="icon">
                <svg
                    className="icon-10"
                    width="10"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <g>
                        <circle cx="12" cy="12" r="8" fill="currentColor" />
                    </g>
                </svg>
            </i>

            <i
                className="sidenav-mini-icon"
                data-bs-toggle="tooltip"
                title={item.label}
                data-bs-placement="right"
            >
                {item.label.charAt(0)}
            </i>

            <span className="item-name">{item.label}</span>
        </NavLink>
    );
}

export default Sidebar;